<template>
  <section>
    <div
      class="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
    >
      <div class="text-start">
        <h2 class="text-xl font-bold text-gray-800">Relatórios recentes</h2>

        <p class="mt-1 text-sm text-gray-500">
          Acompanhe os relatórios de visita técnica, continue rascunhos e
          finalize documentos.
        </p>
      </div>

      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <ReportSearchBar
          :model-value="search"
          placeholder="Buscar por relatório, empresa, técnico..."
          @update:model-value="setSearch"
        />

        <div
          class="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1"
        >
          <button
            v-for="option in statusOptions"
            :key="option.value"
            type="button"
            class="rounded-lg px-3 py-2 text-sm transition whitespace-nowrap"
            :class="
              statusFilter === option.value
                ? 'bg-white text-[#2f8f2f] shadow-sm'
                : 'text-gray-500 hover:text-gray-800'
            "
            @click="setStatus(option.value)"
          >
            {{ option.label }}
          </button>
        </div>

        <ReportViewModeToggle
          :model-value="viewMode"
          @update:model-value="setViewMode"
        />

        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#2f8f2f] to-[#5dbb45] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md"
          @click="openCreateDrawer"
        >
          <PhPlus class="h-5 w-5" />
          Novo relatório
        </button>
      </div>
    </div>

    <ReportTable
      v-if="viewMode === 'table'"
      :reports="filteredReports"
      @build="buildReport"
      @edit="openEditDrawer"
      @finish="finishReport"
      @reopen="reopenReport"
      @archive="archiveReport"
      @restore="restoreReport"
      @open-pdf="openPdf"
      @clone="cloneReport"
    />

    <ReportCards
      v-else
      :reports="filteredReports"
      @build="buildReport"
      @edit="openEditDrawer"
      @finish="finishReport"
      @reopen="reopenReport"
      @archive="archiveReport"
      @restore="restoreReport"
      @open-pdf="openPdf"
      @clone="cloneReport"
    />

    <ReportVisitFormDrawer
      v-model="isDrawerOpen"
      :report="selectedReport"
      :clients="visitClients"
      :layouts="layouts"
      :loading="isSaving"
      @submit="saveReport"
      @close="clearSelectedReport"
    />
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { PhPlus } from "@phosphor-icons/vue";
import { showToast as toast } from "@/helpers/UseToasts";

import { dataReport } from "../../store";
import { useRouter } from "vue-router";
import ReportsService from "../../services/reports.service";
import ReportVisitClientsService from "../../services/reportVisitClients.service";

import ReportSearchBar from "../shared/ReportSearchBar.vue";
import ReportViewModeToggle from "../shared/ReportViewModeToggle.vue";
import ReportTable from "../reports/ReportTable.vue";
import ReportCards from "../reports/ReportCards.vue";
import ReportVisitFormDrawer from "../reports/ReportVisitFormDrawer.vue";
import ReportLayoutsService from "../../services/reportLayouts.service";

const isDrawerOpen = ref(false);
const isSaving = ref(false);
const selectedReport = ref(null);
const router = useRouter();

const statusOptions = [
  {
    label: "Todos",
    value: "all",
  },
  {
    label: "Rascunhos",
    value: "draft",
  },
  {
    label: "Finalizados",
    value: "finished",
  },
  {
    label: "Emitidos",
    value: "issued",
  },
  {
    label: "Arquivados",
    value: "archived",
  },
];

const search = computed(() => dataReport.state.filters.reportsSearch);
const statusFilter = computed(() => dataReport.state.filters.status || "all");
const viewMode = computed(() => dataReport.state.reportsViewMode || "table");
const reports = computed(() => dataReport.state.reports);
const visitClients = computed(() => dataReport.state.visitClients);
const layouts = computed(() => dataReport.state.layouts);

const filteredReports = computed(() => {
  const term = search.value.toLowerCase().trim();

  return reports.value.filter((report) => {
    const matchesStatus =
      statusFilter.value === "all" || report.status === statusFilter.value;

    if (!matchesStatus) return false;

    if (!term) return true;

    return [
      report.title,
      report.report_type,
      report.technician_name,
      report.technician_role,
      report.notes,
      report?.client_snapshot?.corporate_name,
      report?.client_snapshot?.trade_name,
      report?.client_snapshot?.cnpj,
      report?.client?.corporate_name,
      report?.client?.trade_name,
      report?.client?.cnpj,
    ]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(term));
  });
});

onMounted(async () => {
  await Promise.all([
    loadReports(),
    loadVisitClients(),
    loadLayouts(),
  ]);
});

async function loadReports() {
  dataReport.commit("setReportsLoading", true);

  try {
    const response = await ReportsService.getAll({
      status: "all",
    });

    const responseData =
      response?.data?.data || response?.data?.reports || response?.data || [];

    dataReport.commit("setReports", responseData);
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível carregar os relatórios recentes.",
      "error"
    );
  } finally {
    dataReport.commit("setReportsLoading", false);
  }
}

async function loadVisitClients() {
  try {
    const response = await ReportVisitClientsService.getAll({
      include_inactive: true,
    });

    const responseData =
      response?.data?.data || response?.data?.clients || response?.data || [];

    dataReport.commit("setVisitClients", responseData);
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível carregar as empresas para criação do relatório.",
      "error"
    );
  }
}

function setSearch(value) {
  dataReport.commit("setReportsSearch", value);
}

function setStatus(value) {
  dataReport.commit("setReportsStatus", value);
}

function setViewMode(mode) {
  dataReport.commit("setReportsViewMode", mode);
}

function openCreateDrawer() {
  selectedReport.value = null;
  dataReport.commit("setSelectedReport", null);
  isDrawerOpen.value = true;
}

function openEditDrawer(report) {
  selectedReport.value = { ...report };
  dataReport.commit("setSelectedReport", report);
  isDrawerOpen.value = true;
}

function clearSelectedReport() {
  selectedReport.value = null;
  dataReport.commit("setSelectedReport", null);
}

async function saveReport({ id, payload, isEdit }) {
  isSaving.value = true;

  try {
    if (isEdit) {
      const response = await ReportsService.update(id, payload);

      const updatedReport = response?.data?.data || response?.data;

      dataReport.commit("updateReport", updatedReport);

      toast("Protege informa:", "Relatório atualizado com sucesso.", "success");
    } else {
      const response = await ReportsService.create(payload);

      const createdReport = response?.data?.data || response?.data;

      dataReport.commit("addReport", createdReport);

      toast("Protege informa:", "Relatório criado com sucesso.", "success");
    }

    isDrawerOpen.value = false;
    clearSelectedReport();
  } catch (error) {
    console.error(error);

    const message =
      error?.response?.data?.message ||
      getFirstValidationError(error) ||
      "Não foi possível salvar o relatório. Verifique os dados e tente novamente.";

    toast("Protege informa:", message, "error");
  } finally {
    isSaving.value = false;
  }
}

async function finishReport(report) {
  const confirmFinish = window.confirm(
    `Deseja realmente finalizar o relatório "${report.title}"?`
  );

  if (!confirmFinish) return;

  try {
    const response = await ReportsService.finish(report.id);
    const updatedReport = response?.data?.data || response?.data;

    dataReport.commit("updateReport", updatedReport);

    toast("Protege informa:", "Relatório finalizado com sucesso.", "success");
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível finalizar o relatório.",
      "error"
    );
  }
}

async function archiveReport(report) {
  const confirmArchive = window.confirm(
    `Deseja realmente arquivar o relatório "${report.title}"?`
  );

  if (!confirmArchive) return;

  try {
    const response = await ReportsService.archive(report.id);
    const updatedReport = response?.data?.data || response?.data;

    dataReport.commit("updateReport", updatedReport);

    toast("Protege informa:", "Relatório arquivado com sucesso.", "success");
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível arquivar o relatório.",
      "error"
    );
  }
}

async function restoreReport(report) {
  const confirmRestore = window.confirm(
    `Deseja realmente restaurar o relatório "${report.title}"?`
  );

  if (!confirmRestore) return;

  try {
    const response = await ReportsService.restore(report.id);
    const updatedReport = response?.data?.data || response?.data;

    dataReport.commit("updateReport", updatedReport);

    toast("Protege informa:", "Relatório restaurado com sucesso.", "success");
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível restaurar o relatório.",
      "error"
    );
  }
}

function getFirstValidationError(error) {
  const errors = error?.response?.data?.errors;

  if (!errors) return null;

  const firstKey = Object.keys(errors)[0];

  if (!firstKey) return null;

  return errors[firstKey]?.[0] || null;
}

function buildReport(report) {
  router.push({
    name: "reports-visit-builder",
    params: {
      id: report.id,
    },
  });
}

async function reopenReport(report) {
  const confirmReopen = window.confirm(
    `Deseja reabrir o relatório "${report.title}" como rascunho?`
  );

  if (!confirmReopen) return;

  try {
    const response = await ReportsService.reopen(report.id);
    const updatedReport = response?.data?.data || response?.data;

    dataReport.commit("updateReport", updatedReport);

    toast(
      "Protege informa:",
      "Relatório reaberto como rascunho.",
      "success"
    );
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível reabrir o relatório.",
      "error"
    );
  }
}

async function loadLayouts() {
  try {
    const response = await ReportLayoutsService.getAll({
      status: "active",
    });

    const responseData =
      response?.data?.data ||
      response?.data?.layouts ||
      response?.data ||
      [];

    dataReport.commit("setLayouts", responseData);
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível carregar os layouts disponíveis.",
      "error"
    );
  }
}

function getPdfUrl(report) {
  console.log(
    report?.pdf_url,
    report?.pdf_path
  );
  
  if (report?.pdf_url) {
    return report.pdf_url;
  }

  if (!report?.pdf_path) {
    return "";
  }

  const baseUrl =
    process.env.VUE_APP_FILES_URL ||
    process.env.VUE_APP_BASE_FILES_URL ||
    "";

  if (!baseUrl) {
    return "";
  }

  return `${baseUrl.replace(/\/$/, "")}/${String(report.pdf_path).replace(/^\//, "")}`;
}

function openPdf(report) {
  const url = getPdfUrl(report);

  console.log(url);

  if (!url) {
    toast(
      "Protege informa:",
      "Este relatório possui PDF gerado, mas não foi possível localizar a URL pública.",
      "info"
    );

    return;
  }

  window.open(url, "_blank");
}

async function cloneReport(report) {
  const confirmClone = window.confirm(
    `Deseja criar uma cópia editável do relatório "${report.title}"? O relatório original será mantido intacto.`
  );

  if (!confirmClone) return;

  try {
    const response = await ReportsService.clone(report.id);
    const clonedReport = response?.data?.data || response?.data;

    if (clonedReport) {
      dataReport.commit("addReport", clonedReport);

      toast(
        "Protege informa:",
        "Cópia editável criada com sucesso.",
        "success"
      );

      router.push({
        name: "reports-visit-builder",
        params: {
          id: clonedReport.id,
        },
      });

      return;
    }

    toast(
      "Protege informa:",
      "Cópia criada, mas não foi possível abrir o novo relatório automaticamente.",
      "info"
    );
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      error?.response?.data?.message || "Não foi possível criar a cópia editável.",
      "error"
    );
  }
}



</script>
